/**
 * Central feature block definitions
 * All dashboard feature blocks are defined here
 */

import type { ComponentType } from 'react';
import {
  Users,
  ShoppingCart,
  HardHat,
  ClipboardCheck,
  CreditCard,
  Clock,
  CalendarOff,
  CheckSquare,
  FileText,
  Bug,
  Timer,
  Wallet,
  Calculator,
  Store,
  Warehouse,
} from 'lucide-react';

export type FeatureCategory = 'core' | 'tools' | 'management' | 'hr' | 'system';

export const CATEGORY_LABELS: Record<FeatureCategory, string> = {
  core: '核心業務',
  tools: '業務工具',
  management: '管理',
  hr: '人事',
  system: '系統',
};

export const CATEGORY_COLORS: Record<FeatureCategory, string> = {
  core: 'text-[#0071E3]',
  tools: 'text-[#B4654A]',
  management: 'text-[#5E5CE6]',
  hr: 'text-[#86868B]',
  system: 'text-[#86868B]',
};

export interface FeatureBlock {
  id: string;
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  route: string | null;
  bgColor: string;
  textColor: string;
  category: FeatureCategory;
  defaultHidden?: boolean;
}

export const FEATURE_BLOCKS: FeatureBlock[] = [
  {
    id: 'crm',
    label: '專案管理',
    description: '客戶專案追蹤',
    icon: Users,
    route: '/crm',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    category: 'core',
  },
  {
    id: 'sale',
    label: '訂單管理',
    description: '銷售訂單',
    icon: ShoppingCart,
    route: '/sale',
    bgColor: 'bg-green-50',
    textColor: 'text-green-600',
    category: 'core',
  },
  {
    id: 'construction',
    label: '施工管理',
    description: '施工單排程',
    icon: HardHat,
    route: '/construction',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-600',
    category: 'core',
  },
  {
    id: 'acceptance',
    label: '驗收管理',
    description: '完工驗收',
    icon: ClipboardCheck,
    route: '/acceptance',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-600',
    category: 'core',
  },
  {
    id: 'deposit-orders',
    label: '待收訂金',
    description: '待收訂金報價單',
    icon: Wallet,
    route: '/sale?filter=deposit',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    category: 'tools',
  },
  {
    id: 'showroom',
    label: '展間接待',
    description: 'AI 接待表辨識',
    icon: Store,
    route: '/showroom',
    bgColor: 'bg-cyan-50',
    textColor: 'text-cyan-600',
    category: 'tools',
  },
  {
    id: 'bizcard',
    label: '名片匯入',
    description: 'AI 名片掃描',
    icon: CreditCard,
    route: '/bizcard',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-600',
    category: 'tools',
  },
  {
    id: 'stock',
    label: '餘料庫存',
    description: '石材餘料管理',
    icon: Warehouse,
    route: '/stock',
    bgColor: 'bg-stone-50',
    textColor: 'text-stone-600',
    category: 'tools',
  },
  {
    id: 'approval',
    label: '主管簽核',
    description: '待簽核項目',
    icon: CheckSquare,
    route: '/approval',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600',
    category: 'management',
  },
  {
    id: 'accounting',
    label: '財會管理',
    description: '案件收款追蹤',
    icon: Calculator,
    route: '/accounting',
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-600',
    category: 'management',
  },
  {
    id: 'attendance',
    label: '打卡',
    description: '上下班打卡',
    icon: Clock,
    route: '/attendance',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-600',
    category: 'hr',
    defaultHidden: true,
  },
  {
    id: 'leave',
    label: '請假及簽核',
    description: '請假/加班/簽核',
    icon: CalendarOff,
    route: '/leave-hub',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-600',
    category: 'hr',
    defaultHidden: true,
  },
  {
    id: 'overtime',
    label: '加班管理',
    description: '加班申請',
    icon: Timer,
    route: '/overtime',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    category: 'hr',
    defaultHidden: true,
  },
  {
    id: 'ticket-requirement',
    label: '系統需求',
    description: '系統需求單',
    icon: FileText,
    route: '/ticket?type=requirement',
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-600',
    category: 'system',
  },
  {
    id: 'ticket-bug',
    label: 'BUG回報',
    description: 'BUG處理單',
    icon: Bug,
    route: '/ticket?type=bug',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600',
    category: 'system',
  },
];

export function getFeatureBlock(id: string): FeatureBlock | undefined {
  return FEATURE_BLOCKS.find((b) => b.id === id);
}
